import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { App, Button, Card, ColorPicker, Form, Input, Modal, Popconfirm, Space, Table, Tag, TreeSelect } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import type { TagItem } from '@crm/shared';
import { PageHeader } from '../../components/PageHeader';
import { errorMessage } from '../../api/client';
import { useCreateTag, useDeleteTag, useTags, useUpdateTag } from '../../features/audience/api';
import { EmptyState, tableLocale } from '../../components/states';
import { plural } from '../../utils/format';
import { ProjectFormItem, useProjectColumn } from '../../components/ProjectSelect';
import { useProjectScope } from '../../features/projects/scope';

const PRESETS = ['#1677ff', '#52c41a', '#faad14', '#f5222d', '#722ed1', '#13c2c2', '#eb2f96', '#fa8c16', '#8c8c8c'];

type TagNode = TagItem & { children?: TagNode[] };
type TagForm = { name: string; color?: string | null; parentId?: string | null; projectId?: string | null };
type ParentOption = { value: string; title: string; disabled: boolean; children?: ParentOption[] };

/** Turns the flat, path-sorted tag list into a tree for the table and the parent picker. */
function buildTree(tags: TagItem[]): TagNode[] {
  const nodes = new Map<string, TagNode>(tags.map((t) => [t.id, { ...t }]));
  const roots: TagNode[] = [];
  for (const node of nodes.values()) {
    const parent = node.parentId ? nodes.get(node.parentId) : undefined;
    if (parent) (parent.children ??= []).push(node);
    else roots.push(node);
  }
  return roots;
}

/** Ids of a tag and everything nested under it: none of them can become its parent. */
function subtreeIds(tags: TagItem[], id: string): Set<string> {
  const ids = new Set([id]);
  for (let grew = true; grew; ) {
    grew = false;
    for (const t of tags) if (t.parentId && ids.has(t.parentId) && !ids.has(t.id)) { ids.add(t.id); grew = true; }
  }
  return ids;
}

export function TagsPage() {
  const { message } = App.useApp();
  const tags = useTags();
  const create = useCreateTag();
  const update = useUpdateTag();
  const remove = useDeleteTag();
  const [editing, setEditing] = useState<TagItem | 'new' | null>(null);
  const [collapsed, setCollapsed] = useState<string[]>([]);
  const [form] = Form.useForm<TagForm>();
  const scope = useProjectScope();
  const projectColumn = useProjectColumn<TagNode>();
  const all = useMemo(() => tags.data ?? [], [tags.data]);
  const tree = useMemo(() => buildTree(all), [all]);
  const parentIds = useMemo(() => all.filter((t) => all.some((c) => c.parentId === t.id)).map((t) => t.id), [all]);
  const blocked = useMemo(() => (editing && editing !== 'new' ? subtreeIds(all, editing.id) : new Set<string>()), [all, editing]);
  const parentOptions = useMemo(() => {
    const toOption = (n: TagNode): ParentOption => ({
      value: n.id,
      title: n.name,
      disabled: blocked.has(n.id),
      children: n.children?.map(toOption),
    });
    return tree.map(toOption);
  }, [tree, blocked]);

  const openNew = (parentId: string | null = null) => {
    form.resetFields();
    form.setFieldsValue({ parentId, projectId: scope.current?.id ?? null });
    setEditing('new');
  };

  const save = async (v: TagForm) => {
    const input = { ...v, parentId: v.parentId ?? null };
    try {
      if (editing === 'new') await create.mutateAsync(input);
      else if (editing) await update.mutateAsync({ id: editing.id, ...input });
      message.success(editing === 'new' ? 'Tag created' : 'Tag saved');
      setEditing(null);
    } catch (e) {
      message.error(errorMessage(e));
    }
  };

  const filterFor = (id: string) => encodeURIComponent(JSON.stringify({ combinator: 'and', conditions: [{ kind: 'tag', op: 'any_of', tagIds: [id] }] }));

  return (
    <div className="page">
      <PageHeader
        title="Tags"
        subtitle="Manual labels you can apply to contacts individually or in bulk, and filter or target by. Nest tags under each other to group them."
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={() => openNew()}>
            New tag
          </Button>
        }
      />
      <Card>
      <Table<TagNode>
        rowKey="id"
        loading={tags.isLoading}
        locale={tableLocale(tags, <EmptyState description="No tags yet. Tags are quick labels you can add to contacts and use in filters and campaigns." action={<Button type="primary" icon={<PlusOutlined />} onClick={() => openNew()}>New tag</Button>} />)}
        dataSource={tree}
        pagination={false}
        expandable={{
          expandedRowKeys: parentIds.filter((id) => !collapsed.includes(id)),
          onExpand: (open, t) => setCollapsed((c) => (open ? c.filter((id) => id !== t.id) : [...c, t.id])),
        }}
        columns={[
          { title: 'Tag', dataIndex: 'name', render: (v, t) => <Tag color={t.color ?? undefined}>{v}</Tag> },
          ...projectColumn,
          { title: 'Contacts', dataIndex: 'contactCount', width: 140, render: (n: number, t) => (n ? <Link to={`/contacts?filter=${filterFor(t.id)}`}>{n.toLocaleString()}</Link> : 0) },
          {
            key: 'actions',
            align: 'right',
            render: (_, t) => (
              <Space>
                <Button size="small" icon={<PlusOutlined />} onClick={() => openNew(t.id)}>Sub-tag</Button>
                <Button size="small" onClick={() => { form.setFieldsValue({ name: t.name, color: t.color, parentId: t.parentId, projectId: t.projectId }); setEditing(t); }}>Edit</Button>
                <Popconfirm title={`Delete tag "${t.name}"?`} description={deleteNote(t)} okText="Delete" okButtonProps={{ danger: true, loading: remove.isPending }} onConfirm={() => remove.mutateAsync(t.id).then(() => message.success('Tag deleted')).catch((e) => message.error(errorMessage(e)))}>
                  <Button size="small" danger>Delete</Button>
                </Popconfirm>
              </Space>
            ),
          },
        ]}
      />
      </Card>
      <Modal title={editing === 'new' ? 'New tag' : 'Edit tag'} open={!!editing} onCancel={() => setEditing(null)} onOk={() => form.submit()} okText={editing === 'new' ? 'Create' : 'Save'} confirmLoading={create.isPending || update.isPending} destroyOnHidden>
        <Form form={form} layout="vertical" onFinish={save} requiredMark={false} initialValues={{ color: null }}>
          <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Give the tag a name' }]}>
            <Input autoFocus maxLength={60} />
          </Form.Item>
          <Form.Item name="parentId" label="Nest under" extra="Leave empty for a top-level tag.">
            <TreeSelect allowClear showSearch treeNodeFilterProp="title" treeDefaultExpandAll treeData={parentOptions} placeholder="No parent (top level)" />
          </Form.Item>
          <Form.Item name="color" label="Color" getValueFromEvent={(c) => (c ? (typeof c === 'string' ? c : c.toHexString()) : null)}>
            <ColorPicker allowClear presets={[{ label: 'Presets', colors: PRESETS }]} format="hex" />
          </Form.Item>
          <ProjectFormItem />
        </Form>
      </Modal>
    </div>
  );
}

function deleteNote(t: TagNode) {
  const notes = [
    t.contactCount ? `It is removed from ${plural(t.contactCount, 'contact')}.` : null,
    t.children?.length ? `Its ${plural(t.children.length, 'sub-tag')} move up a level.` : null,
  ].filter(Boolean);
  return notes.length ? notes.join(' ') : undefined;
}
